/**
 * localStorage persistence: settings and an autosaved session, kept in separate
 * keys so a corrupt session never costs the user their preferences.
 *
 * Every read is defensive — a browser in private mode can throw on access, and
 * a stored blob from an older version may be missing fields.
 */

import type { AssistId, Edo, Exercise } from '../core';
import { keyById } from '../core';
import { coerceSettings, type Settings } from './settings';

const SETTINGS_KEY = 'slcpt.settings.v2';
const SESSION_KEY = 'slcpt.session.v2';
/** The prototype's single autosave key, read once so old work is not lost. */
const LEGACY_KEY = 'slcpt-autosave-v1';

/** The autosaved exercise plus the assist history that goes with it. */
export interface SavedSession {
  readonly exercise: Exercise;
  readonly assisted: Record<AssistId, boolean>;
}

function read(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota exhausted or storage disabled — autosave is best-effort */
  }
}

/** Load settings, falling back to defaults for anything missing. */
export const loadSettings = (): Settings => coerceSettings(read(SETTINGS_KEY));

/** Persist settings. Called on every settings change. */
export const saveSettings = (settings: Settings): void => write(SETTINGS_KEY, settings);

/** Load the autosaved session, or null when there is nothing usable. */
export function loadSession(): SavedSession | null {
  const j = (read(SESSION_KEY) ?? read(LEGACY_KEY)) as Record<string, any> | null;
  if (!j || !Array.isArray(j.cf) || !j.cf.length) return null;
  const cf: number[] = j.cf.map(Number);
  const cp: (number | null)[] = Array.isArray(j.cp)
    ? cf.map((_, i) => (j.cp[i] == null ? null : Number(j.cp[i])))
    : cf.map(() => null);
  return {
    exercise: {
      edo: (j.edo === 31 ? 31 : 12) as Edo,
      key: keyById(j.key),
      cfPos: j.cfPos === 'upper' ? 'upper' : 'lower',
      cf,
      cp,
    },
    assisted: {
      labels: !!j.assisted?.labels,
      // The prototype called the allowed-notes assist "ghost".
      allowed: !!(j.assisted?.allowed ?? j.assisted?.ghost),
    },
  };
}

/** Autosave the session. Called after every edit. */
export function saveSession(session: SavedSession): void {
  write(SESSION_KEY, {
    key: session.exercise.key.id,
    edo: session.exercise.edo === 31 ? 31 : 12,
    cfPos: session.exercise.cfPos,
    cf: session.exercise.cf,
    cp: session.exercise.cp,
    assisted: session.assisted,
  });
}

/** Forget the autosaved session — used when the user asks for a clean slate. */
export function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* nothing to do */
  }
}
